import type { AuthError, EmailOtpType, Session } from "@supabase/supabase-js"

/**
 * What a link in a Supabase Auth email (password reset, email change, sign-up
 * confirmation, magic link) brought to the page it opened:
 *
 * - `tokenHash`: the new links (`?token_hash=…&type=…`). They lead straight
 *   here, on our domain, and the page verifies the token itself (see
 *   verifyAuthEmailLinkOnce).
 * - The old links went to Supabase's `/auth/v1/verify`, which verified the
 *   token and came back with the result in the fragment: `session`
 *   (`#access_token=…&type=…`, picked up by supabase-js), `pending` (`#message=…`:
 *   the first of the two links confirming an email change), or `error`
 *   (`error_code`, e.g. `otp_expired`, in the fragment or the query).
 *
 * NOTE: Emails sent before the templates changed still carry the old links,
 * so both kinds stay supported.
 */
export type AuthEmailLink =
  | { kind: "tokenHash"; tokenHash: string; type: EmailOtpType | undefined }
  | { kind: "session"; type: string | undefined }
  | { kind: "pending" }
  | { kind: "error"; code: string }
  | { kind: "none" }

const EMAIL_OTP_TYPES: readonly EmailOtpType[] = [
  "email",
  "email_change",
  "invite",
  "magiclink",
  "recovery",
  "signup",
]

const isEmailOtpType = (value: string | undefined): value is EmailOtpType =>
  EMAIL_OTP_TYPES.includes(value as EmailOtpType)

/**
 * A parameter of the query or the fragment.
 *
 * NOTE: Found anywhere after the first `?` or `#`, not only through
 * URLSearchParams: an email template appending `?token_hash=…` to a redirect
 * URL that has a query already makes a second `?` (`?event=email?token_hash=…`),
 * which URLSearchParams reads as part of the first parameter's value.
 */
function findParam(url: string, name: string): string | undefined {
  const match = url.match(new RegExp(`[?&#]${name}=([^&#]*)`))
  if (!match) return undefined
  try {
    return decodeURIComponent(match[1].replace(/\+/g, " ")) || undefined
  } catch {
    return undefined
  }
}

export function parseAuthEmailLink(
  url: string | null | undefined,
): AuthEmailLink {
  if (!url) return { kind: "none" }

  const tokenHash = findParam(url, "token_hash")
  if (tokenHash) {
    const type = findParam(url, "type")
    return {
      kind: "tokenHash",
      tokenHash,
      type: isEmailOtpType(type) ? type : undefined,
    }
  }

  const errorCode = findParam(url, "error_code") ?? findParam(url, "error")
  if (errorCode) return { kind: "error", code: errorCode }

  const hashIndex = url.indexOf("#")
  const hash = hashIndex === -1 ? "" : url.slice(hashIndex)
  if (findParam(hash, "access_token")) {
    return { kind: "session", type: findParam(hash, "type") }
  }
  if (findParam(hash, "message")) return { kind: "pending" }

  return { kind: "none" }
}

declare global {
  interface Window {
    /** The address the app was opened with, see index.html. */
    vmLandingUrl?: string
  }
}

/**
 * The auth email link that opened the app on `pathname`, or `none` (opened on
 * another page, or no link).
 *
 * NOTE: Read from the address the app was opened with (saved in index.html,
 * which also takes a `token_hash` out of the address bar before any tracker or
 * supabase-js reads it).
 */
export function authEmailLinkFor(pathname: string): AuthEmailLink {
  const landingUrl = window.vmLandingUrl ?? window.location.href
  let landingPathname: string
  try {
    landingPathname = new URL(landingUrl).pathname
  } catch {
    return { kind: "none" }
  }
  const trim = (path: string) => path.replace(/\/+$/, "")
  if (trim(landingPathname) !== trim(pathname)) return { kind: "none" }
  return parseAuthEmailLink(landingUrl)
}

type VerifyResult = { session: Session | null; error: AuthError | null }
type VerifyOtp = (params: {
  token_hash: string
  type: EmailOtpType
}) => Promise<{ data: { session: Session | null }; error: AuthError | null }>

const verifications = new Map<string, Promise<VerifyResult>>()

/**
 * Verifies a `token_hash` link, once per token: a token works only once, and
 * React (StrictMode, or coming back to the page) may ask again.
 *
 * Resolves with the session (none after the first of the two links confirming
 * an email change), or the error (e.g. expired or used already).
 */
export function verifyAuthEmailLinkOnce(
  verifyOtp: VerifyOtp,
  tokenHash: string,
  type: EmailOtpType,
): Promise<VerifyResult> {
  const key = `${type}:${tokenHash}`
  let verification = verifications.get(key)
  if (!verification) {
    verification = verifyOtp({ token_hash: tokenHash, type }).then(
      ({ data, error }) => ({ session: data.session, error }),
    )
    verifications.set(key, verification)
  }
  return verification
}
